'use client';

import { createContext, useContext } from 'react';

import { ApiSchemas, useApiV1Ctx } from '@/components/providers/v1-api';
import { useOfficeCtx } from '@/components/providers/v1-office-provider';

export type OfficeRegion = ApiSchemas['OfficeRegionRead'];
type OfficeRegionCreate = ApiSchemas['OfficeRegionCreate'];

interface ServiceAreasStore {
  serviceAreas: OfficeRegion[];
  update: (v: OfficeRegion) => void;
  create: (v: OfficeRegionCreate) => void;
  remove: (id: string) => void;
}

const ServiceAreasContext = createContext<ServiceAreasStore | null>(null);

export function useServiceAreasCtx() {
  const ctx = useContext(ServiceAreasContext);
  if (!ctx) {
    throw new Error('useServiceAreasCtx must be used within a ServiceAreasProvider');
  }
  return ctx;
}

export function ServiceAreasProvider({ children }: { children: React.ReactNode }) {
  const { office, refetch } = useOfficeCtx();
  const { api } = useApiV1Ctx();

  const createMutation = api.officeRegions.createOfficeRegionsPost.useMutation(undefined, {
    onSuccess: refetch,
  });
  const updateMutation = api.officeRegions.updateOfficeRegionsRegionIdPatch.useMutation(undefined, {
    onSuccess: refetch,
  });
  const removeMutation = api.officeRegions.removeOfficeRegionsRegionIdDelete.useMutation(undefined, {
    onSuccess: refetch,
  });

  const update = (v: OfficeRegion) => {
    updateMutation.mutate({
      path: { region_id: v.id },
      body: {
        name: v.name,
        zip_codes: v.zip_codes,
      },
    });
  };

  const create = (v: OfficeRegionCreate) => {
    createMutation.mutate({
      body: {
        ...v,
        office_id: office.id,
      },
    });
  };

  const remove = (id: string) => {
    removeMutation.mutate({ path: { region_id: id } });
  };

  return (
    <ServiceAreasContext.Provider value={{ serviceAreas: office.regions, update, create, remove }}>
      {children}
    </ServiceAreasContext.Provider>
  );
}
